/**
 * @fileoverview Variant-group slug extraction (MOR-886)
 *
 * Split out of `VariantPicker.tsx` (react-refresh/only-export-components) —
 * pure helpers for detecting which variant groups a template references,
 * and which of those are orphaned (no matching org-wide group).
 */

export interface VariantGroup {
  slug: string;
  name: string;
  options: Array<{ value: string; label: string }>;
  /**
   * Optional data trigger (MOR-886): the group's active option is resolved
   * per generated document from this field's value. Surfaced as an "Auto"
   * chip when `bindingAware` is set.
   */
  fieldBinding?: {
    fieldKey: string;
    mappings: Array<{ value: string; optionValue: string }>;
    defaultOptionValue?: string;
  } | null;
}

/**
 * Extract referenced variant-group slugs from a template's HTML. Scans for
 * the `data-group-slug` attribute the variantBlock NodeView serialises.
 */
export function extractVariantGroupSlugs(html?: string | null): Set<string> {
  const slugs = new Set<string>();
  if (!html) return slugs;
  const re = /data-group-slug="([^"]+)"/g;
  for (const m of html.matchAll(re)) slugs.add(m[1]);
  return slugs;
}

/**
 * Variant-group slugs a template references that have no matching
 * org-wide group (deleted group, or the groups query hasn't loaded).
 * Exported so callers can render the orphan warning in their own layout
 * slot rather than inline with the picker.
 */
export function getOrphanVariantSlugs(
  templateHtml: string | null | undefined,
  groups: VariantGroup[],
): string[] {
  const referenced = extractVariantGroupSlugs(templateHtml);
  const known = new Set(groups.map((g) => g.slug));
  return [...referenced].filter((s) => !known.has(s));
}
